import * as tf from "@tensorflow/tfjs";
import "@tensorflow/tfjs-backend-webgl";
import "@tensorflow/tfjs-backend-wasm";
import { setWasmPaths } from "@tensorflow/tfjs-backend-wasm";
import * as faceLandmarksDetection from "@tensorflow-models/face-landmarks-detection";
import * as cocoSsd from "@tensorflow-models/coco-ssd";
import { ref, markRaw } from "vue";
import { Room, RoomEvent, Track } from "livekit-client";
import store from "@/store";
import { apiPost, apiPatch } from "@/utils/api";

// ── Native browser objects — never inside Vuex state ──
let _cameraStream = null;
let _screenStream = null;
let _detector = null;
let _room = null;
let _adminRoom = null;
let _keydownHandler = null;
let _visibilityHandler = null;
let _blurHandler = null;
let _rightClickHandler = null;
let _copyHandler = null;
let _pasteHandler = null;
let _cutHandler = null;
let _objectDetector = null;
let _objectDetectInFlight = false;
let _objectCanvas = null;
let _objectCanvasCtx = null;
let _lastObjectDetectionTime = 0;
let _objectTargetW = 0;
let _verifyInterval = null;

// Focus/Visibility suppression
let _suppressFocusViolations = false;
let _suppressTimeout = null;

const suppressFocus = (ms = 3000) => {
  if (_suppressTimeout) clearTimeout(_suppressTimeout);
  _suppressFocusViolations = true;
  _suppressTimeout = setTimeout(() => {
    _suppressFocusViolations = false;
    _suppressTimeout = null;
  }, ms);
};

// Reactive signals
const _streamReady = ref(false);
const _screenReady = ref(false);

const state = () => ({
  roomName: "",
  participantName: "student" + Math.floor(Math.random() * 1000),
  role: "student",

  animationId: null,
  lastDetectionTime: 0,

  isInitialized: false,
  initStatus: "idle",
  hasCamera: false,
  hasMic: false,
  hasScreen: false,
  isTfReady: false,

  showViolationAlert: false,
  violationReason: "",
  lastViolationTime: 0,
  violationCooldown: 3000,
  violationsData: [],

  eyesClosedStart: null,
  lookingAwayStart: null,
  multipleFaceStart: null,
  noFaceStart: null,

  // markRaw participants so Vue never proxies LiveKit objects
  remoteParticipants: [],
  listenersActive: false,
  isExamStarted: false,

  objectDetectedStart: null,
  detectedObjects: [],
  lastFaceVerificationStatus: null,
});

const getters = {
  remoteParticipants: (state) => state.remoteParticipants,
  cameraStream: () => {
    // eslint-disable-next-line no-unused-expressions
    _streamReady.value;
    return _cameraStream;
  },
  screenStream: () => {
    _screenReady.value;
    return _screenStream;
  },
  hasCamera: (state) => {
    _streamReady.value;
    return state.hasCamera && _cameraStream && _cameraStream.active;
  },
  hasMic: (state) => {
    _streamReady.value;
    return state.hasMic && _cameraStream && _cameraStream.active;
  },
  hasScreen: (state) => {
    _screenReady.value;
    return state.hasScreen && _screenStream && _screenStream.active;
  },
  isTfReady: (state) => state.isTfReady,
  canProceed: (state, getters) =>
    getters.hasCamera && getters.hasMic && getters.hasScreen,
  isInitialized: (state) => state.isInitialized,
  initStatus: (state) => state.initStatus,
  isInitializing: (state) =>
    state.initStatus === "idle" || state.initStatus === "loading",
  showViolationAlert: (state) => state.showViolationAlert,
  violationReason: (state) => state.violationReason,
  violationsData: (state) => state.violationsData,
  isExamStarted: (state) => state.isExamStarted,
};

const mutations = {
  // Replace the entire array with a fresh array of markRaw participants
  // Fresh array reference forces Vue to detect the change
  SET_REMOTE_PARTICIPANTS(state, participants) {
    state.remoteParticipants = participants.map((p) => markRaw(p));
  },

  SET_ROOM_INFO(state, { roomName, participantName, role }) {
    if (roomName) state.roomName = roomName;
    if (participantName) state.participantName = participantName;
    if (role) state.role = role;
  },
  SET_ANIMATION_ID(state, id) {
    state.animationId = id;
  },
  SET_LAST_DETECTION_TIME(state, t) {
    state.lastDetectionTime = t;
  },
  SET_INITIALIZED(state, val) {
    state.isInitialized = val;
  },
  SET_INIT_STATUS(state, val) {
    state.initStatus = val;
  },
  SET_HAS_CAMERA(state, val) {
    state.hasCamera = val;
  },
  SET_HAS_MIC(state, val) {
    state.hasMic = val;
  },
  SET_HAS_SCREEN(state, val) {
    state.hasScreen = val;
  },
  SET_TF_READY(state, val) {
    state.isTfReady = val;
  },
  SET_VIOLATION_ALERT(state, { show, reason }) {
    state.showViolationAlert = show;
    state.violationReason = reason || "";
  },
  PUSH_VIOLATION(state, violation) {
    state.violationsData.push(violation);
  },
  SET_LAST_VIOLATION_TIME(state, time) {
    state.lastViolationTime = time;
  },
  SET_EYES_CLOSED_START(state, val) {
    state.eyesClosedStart = val;
  },
  SET_LOOKING_AWAY_START(state, val) {
    state.lookingAwayStart = val;
  },
  SET_MULTIPLE_FACE_START(state, val) {
    state.multipleFaceStart = val;
  },
  SET_NO_FACE_START(state, val) {
    state.noFaceStart = val;
  },
  SET_LISTENERS(state, val) {
    state.listenersActive = val;
  },
  SET_IS_EXAM_STARTED(state, val) {
    state.isExamStarted = val;
  },
  SET_CAMERA_STREAM(state, stream) {
    _cameraStream = stream;
    _streamReady.value = !!stream;
    state.hasCamera = !!stream;
    state.hasMic = !!stream;
  },
  SET_SCREEN_STREAM(state, stream) {
    _screenStream = stream;
    _screenReady.value = !!stream;
    state.hasScreen = !!stream;
  },
  SET_OBJECT_DETECTED_START(state, val) {
    state.objectDetectedStart = val;
  },
  SET_DETECTED_OBJECTS(state, objects) {
    state.detectedObjects = objects;
  },
  SET_LAST_FACE_VERIFICATION_STATUS(state, status) {
    state.lastFaceVerificationStatus = status;
  },
  RESET(state) {
    _streamReady.value = false;
    _screenReady.value = false;
    _cameraStream = null;
    _screenStream = null;
    state.remoteParticipants = [];
    state.animationId = null;
    state.lastDetectionTime = 0;
    state.isInitialized = false;
    state.initStatus = "idle";
    state.hasCamera = false;
    state.hasMic = false;
    state.hasScreen = false;
    state.isTfReady = false;
    state.showViolationAlert = false;
    state.violationReason = "";
    state.violationsData = [];
    state.lastViolationTime = 0;
    state.eyesClosedStart = null;
    state.lookingAwayStart = null;
    state.multipleFaceStart = null;
    state.noFaceStart = null;
    state.listenersActive = false;
    state.isExamStarted = false;
  },
};

// Helper — call this after any participant/track change to sync store
function syncParticipants(commit) {
  if (!_adminRoom) return;

  const list = [];

  _adminRoom.remoteParticipants.forEach((participant) => {
    let publications = [];
    const pubs = participant.trackPublications;

    if (pubs instanceof Map) {
      publications = Array.from(pubs.values());
    } else if (pubs && typeof pubs.values === "function") {
      publications = Array.from(pubs.values());
    } else if (pubs && typeof pubs === "object") {
      publications = Object.values(pubs);
    }

    // Map publications to a cleaner format with source normalization
    const cleanPublications = publications.map((pub) => {
      let sourceStr = String(pub.source);

      // Map numeric sources to strings if needed
      if (pub.source === Track.Source.Camera) sourceStr = "camera";
      else if (pub.source === Track.Source.ScreenShare)
        sourceStr = "screen_share";
      else if (pub.source === Track.Source.Microphone) sourceStr = "microphone";

      return {
        trackSid: pub.trackSid,
        kind: pub.kind,
        source: sourceStr,
        trackName: pub.trackName || "",
        isSubscribed: pub.isSubscribed,
        videoTrack: pub.videoTrack || pub.track || null,
        audioTrack: pub.audioTrack || null,
        track: pub.track || null,
      };
    });

    // create SAFE plain object
    list.push({
      identity: participant.identity,
      name: participant.name,
      sid: participant.sid,
      publications: cleanPublications,
      participantRef: markRaw(participant),
      videoTrackPublications: participant.videoTrackPublications,
    });
  });

  commit("SET_REMOTE_PARTICIPANTS", list);
}

const actions = {
  // ── ADMIN: Connect as observer ──
  async connectAsAdmin({ commit }, { roomName }) {
    // Disconnect previous admin room if any
    if (_adminRoom) {
      _adminRoom.disconnect();
      _adminRoom = null;
    }

    commit("SET_REMOTE_PARTICIPANTS", []);

    _adminRoom = markRaw(
      new Room({
        adaptiveStream: true,
        dynacast: true,
        autoSubscribe: true,
      })
    );

    const res = await apiPost("/proctoring/create-room/", {
      room_name: roomName,
      participant_name: store.state.user?.full_name,
      role: "admin",
    });

    const data = res.data;

    const token = data.token;

    if (!token) {
      console.error("Full Response Data:", data);
      throw new Error("Token not found in API response");
    }
    // 1. ATTACH LISTENERS FIRST
    // Sync whoever is already in the room
    syncParticipants(commit);

    // New participant joins
    _adminRoom.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log("[Admin] ParticipantConnected:", participant.identity);
      syncParticipants(commit);
    });

    // Participant leaves
    _adminRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
      console.log("[Admin] ParticipantDisconnected:", participant.identity);
      syncParticipants(commit);
    });

    // Track published by participant
    _adminRoom.on(RoomEvent.TrackPublished, (pub, participant) => {
      console.log(
        "[Admin] TrackPublished:",
        participant.identity,
        pub.kind,
        pub.source
      );
      syncParticipants(commit);
    });

    // Track subscribed — this is when video is actually available
    _adminRoom.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
      console.log(
        "[Admin] TrackSubscribed:",
        participant.identity,
        pub.kind,
        pub.source
      );
      syncParticipants(commit);
    });

    _adminRoom.on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => {
      console.log("[Admin] TrackUnsubscribed:", participant.identity);
      syncParticipants(commit);
    });

    // Connection quality / metadata / permission updates
    _adminRoom.on(RoomEvent.ParticipantMetadataChanged, () => {
      syncParticipants(commit);
    });

    _adminRoom.on(RoomEvent.ParticipantPermissionsChanged, () => {
      syncParticipants(commit);
    });

    _adminRoom.on(RoomEvent.Reconnected, () => {
      syncParticipants(commit);
    });

    // 2. NOW CONNECT
    console.log("[Admin] connecting to room:", roomName);
    try {
      const protocol =
        window.location.protocol === "https:" ? "wss://" : "https://";
      const host =
        process.env.VUE_APP_LIVEKIT_URL || "livekit.erp.onlinejain.com";
      const liveKitUrl = `${protocol}${host}`;
      await _adminRoom.connect(liveKitUrl, token);
      console.log("[Admin] connected successfully to", liveKitUrl);
    } catch (err) {
      console.error("ADMIN ROOM CONNECT FAILED:", err);
    }
    console.log(
      "[Admin] connected. Remote participants:",
      _adminRoom.remoteParticipants.size
    );

    // Final sync after connection
    syncParticipants(commit);
  },

  // ── STEP 1: Called once on ExamWaitingView mount ──
  async initAll(
    { state, commit, dispatch },
    { roomName, participantName, role }
  ) {
    if (state.isInitialized && _cameraStream && _cameraStream.active) {
      return { success: true };
    }

    commit("SET_ROOM_INFO", { roomName, participantName, role });
    commit("SET_INIT_STATUS", "loading");

    try {
      await dispatch("initCamera");
      commit("SET_INIT_STATUS", "ready");
      commit("SET_INITIALIZED", true);

      // Start peripheral proctoring (keyboard, window, etc.)
      dispatch("startProctoring");

      setTimeout(() => dispatch("initTensorFlow"), 500);
      return { success: true };
    } catch (err) {
      commit("SET_INIT_STATUS", "error");
      throw err;
    }
  },

  async initTensorFlow({ commit }) {
    try {
      const ua = (navigator.userAgent || "").toLowerCase();
      const isFirefox = ua.includes("firefox");

      setWasmPaths(
        "https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-backend-wasm@4.22.0/dist/"
      );

      try {
        await tf.setBackend(isFirefox ? "wasm" : "webgl");
      } catch (e) {
        console.warn("[TF] Failed to set preferred backend, trying wasm:", e);
        await tf.setBackend("webgl");
      }
      await tf.ready();

      const model = faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
      _detector = await faceLandmarksDetection.createDetector(model, {
        runtime: "mediapipe",
        solutionPath: "https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh",
        maxFaces: 2,
      });

      _objectDetector = await cocoSsd.load({ base: "mobilenet_v2" });

      commit("SET_TF_READY", true);
    } catch (err) {
      console.error("TensorFlow init failed:", err);
    }
  },

  async initCamera({ commit, dispatch }) {
    if (_cameraStream && _cameraStream.active) {
      _streamReady.value = true;
      commit("SET_HAS_CAMERA", true);
      commit("SET_HAS_MIC", true);
      await dispatch("_publishCamera");
      return;
    }

    suppressFocus(5000);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: true,
      });

      commit("SET_CAMERA_STREAM", stream);
      await dispatch("_publishCamera");
    } catch (err) {
      _cameraStream = null;
      _streamReady.value = false;
      commit("SET_HAS_CAMERA", false);
      commit("SET_HAS_MIC", false);

      if (err.name === "NotAllowedError") {
        throw {
          type: "camera_blocked",
          message:
            "Camera & microphone access was blocked. Please allow access in your browser settings and refresh.",
        };
      } else if (err.name === "NotFoundError") {
        throw {
          type: "camera_missing",
          message: "No camera found. Please connect a webcam and refresh.",
        };
      } else {
        throw {
          type: "camera_error",
          message: "Could not access camera: " + err.message,
        };
      }
    } finally {
      suppressFocus(2000);
    }
  },

  async requestScreenShare({ commit, state, dispatch }) {
    if (_screenStream && _screenStream.active) {
      commit("SET_HAS_SCREEN", true);
      await dispatch("_publishScreen");
      return { success: true };
    }

    suppressFocus(10000);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "monitor" },
        audio: false,
      });

      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      const surface = settings.displaySurface;
      const label = (track.label || "").toLowerCase();

      const isEntireScreen =
        surface === "monitor" ||
        (!surface &&
          (label.includes("screen") ||
            label.includes("display") ||
            label.includes("monitor") ||
            label.includes(":0") ||
            label.includes("entire")));

      if (!isEntireScreen) {
        stream.getTracks().forEach((t) => t.stop());
        commit("SET_HAS_SCREEN", false);
        return {
          success: false,
          message:
            "You selected a window or tab. You must share your Entire Screen. Please try again.",
        };
      }

      track.onended = async () => {
        if (state.isExamStarted) {
          suppressFocus(10000);
          const userAgreed = window.confirm(
            "WARNING: Screen sharing is required for this exam. Stopping screen share will terminate your exam. Do you want to terminate?"
          );
          if (userAgreed) {
            dispatch("terminateExam");
          } else {
            // Re-request immediately
            const result = await dispatch("requestScreenShare");
            if (!result || !result.success) {
              // If they cancel the picker or it fails, kill the exam
              dispatch("terminateExam");
            }
          }
        } else {
          commit("SET_SCREEN_STREAM", null);
        }
      };

      commit("SET_SCREEN_STREAM", stream);
      await dispatch("_publishScreen");
      return { success: true };
    } catch (err) {
      commit("SET_SCREEN_STREAM", null);

      if (state.isExamStarted) {
        dispatch("terminateExam");
      }

      if (err.name === "NotAllowedError") {
        return {
          success: false,
          message:
            "Screen sharing was cancelled. You must share your screen to take this exam.",
        };
      }
      return {
        success: false,
        message: err.message || "Screen sharing failed. Please try again.",
      };
    } finally {
      suppressFocus(3000);
    }
  },

  // ── STEP 2: Called on Proceed click ──
  async connectRoom({ state, commit, dispatch }) {
    if (_room) {
      await dispatch("_publishCamera");
      await dispatch("_publishScreen");
      return;
    }

    _room = markRaw(new Room());

    const res = await apiPost("/proctoring/create-room/", {
      room_name: state.roomName,
      participant_name: state.participantName,
      role: state.role,
    });

    const data = res.data;
    const token = data.token || (data.data && data.data.token);

    if (!token) {
      console.error("[Student] Token not found in API response:", data);
      throw new Error(data.message || "Failed to fetch token from server.");
    }

    const protocol =
      window.location.protocol === "https:" ? "wss://" : "https://";
    const host =
      process.env.VUE_APP_LIVEKIT_URL || "livekit.erp.onlinejain.com";
    const liveKitUrl = `${protocol}${host}`;

    // 1. ATTACH LISTENERS BEFORE CONNECT
    _room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log("[Student] ParticipantConnected:", participant.identity);
    });

    _room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      console.log("[Student] ParticipantDisconnected:", participant.identity);
    });

    _room.on(RoomEvent.TrackPublished, (pub, participant) => {
      console.log(
        "[Student] TrackPublished:",
        participant.identity,
        pub.kind,
        pub.source
      );
    });

    _room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
      console.log(
        "[Student] TrackSubscribed:",
        participant.identity,
        pub.kind,
        pub.source
      );
    });

    _room.on(RoomEvent.Disconnected, (reason) => {
      console.log("[Student] Room Disconnected:", reason);
      commit("SET_IS_EXAM_STARTED", false);
    });

    console.log(
      "[Student] connecting to room:",
      liveKitUrl,
      "RoomName:",
      state.roomName
    );
    await _room.connect(liveKitUrl, token);
    commit("SET_IS_EXAM_STARTED", true);

    console.log("[Student] connected successfully to", liveKitUrl);

    // ---------------------------
    // Publish CAMERA
    // ---------------------------
    await dispatch("_publishCamera");
    await dispatch("_publishScreen");
  },

  async _publishCamera() {
    if (!_room || !_room.localParticipant || !_cameraStream) return;

    const videoTrack = _cameraStream.getVideoTracks()[0];
    const audioTrack = _cameraStream.getAudioTracks()[0];

    const existingVideo = Array.from(
      _room.localParticipant.videoTrackPublications.values()
    ).find((p) => p.trackName === "camera");
    const existingAudio = Array.from(
      _room.localParticipant.audioTrackPublications.values()
    ).find((p) => p.trackName === "microphone");

    if (videoTrack && (!existingVideo || !existingVideo.track)) {
      await _room.localParticipant.publishTrack(videoTrack, {
        name: "camera",
        source: Track.Source.Camera,
      });
      console.log("[Student] camera published");
    }

    if (audioTrack && (!existingAudio || !existingAudio.track)) {
      await _room.localParticipant.publishTrack(audioTrack, {
        name: "microphone",
        source: Track.Source.Microphone,
      });
      console.log("[Student] microphone published");
    }
  },

  async _publishScreen() {
    if (!_room || !_room.localParticipant || !_screenStream) return;

    const screenTrack = _screenStream.getVideoTracks()[0];

    const existingScreen = Array.from(
      _room.localParticipant.videoTrackPublications.values()
    ).find((p) => p.trackName === "screen");

    if (screenTrack && (!existingScreen || !existingScreen.track)) {
      await _room.localParticipant.publishTrack(screenTrack, {
        name: "screen",
        source: Track.Source.ScreenShare,
      });
    }
  },

  startDetection({ state, commit, dispatch }, videoEl) {
    if (state.animationId) {
      cancelAnimationFrame(state.animationId);
      commit("SET_ANIMATION_ID", null);
    }

    if (_verifyInterval) {
      clearInterval(_verifyInterval);
      _verifyInterval = null;
    }

    _verifyInterval = setInterval(() => {
      if (state.isExamStarted) {
        dispatch("performFaceVerification", videoEl);
      }
    }, 20000);

    const DETECTION_INTERVAL = 800;
    const OBJECT_DETECTION_INTERVAL = 3000;

    let currentLoopId = null;

    const loop = async () => {
      if (currentLoopId !== state.animationId) return;

      const now = Date.now();

      if (
        !_detector ||
        !videoEl ||
        videoEl.readyState < 2 ||
        !videoEl.videoWidth ||
        !videoEl.videoHeight
      ) {
        currentLoopId = requestAnimationFrame(loop);
        commit("SET_ANIMATION_ID", currentLoopId);
        return;
      }

      if (now - state.lastDetectionTime >= DETECTION_INTERVAL) {
        commit("SET_LAST_DETECTION_TIME", now);

        try {
          if (now - _lastObjectDetectionTime >= OBJECT_DETECTION_INTERVAL) {
            _lastObjectDetectionTime = now;
            await dispatch("detectProhibitedObjects", videoEl);
          }

          const faces = await _detector.estimateFaces(videoEl);

          if (!faces || faces.length === 0) {
            if (!state.noFaceStart) {
              commit("SET_NO_FACE_START", Date.now());
            } else if (Date.now() - state.noFaceStart > 2000) {
              dispatch("triggerViolation", "No face detected");
            }
          } else {
            commit("SET_NO_FACE_START", null);

            if (faces.length > 1) {
              if (!state.multipleFaceStart) {
                commit("SET_MULTIPLE_FACE_START", Date.now());
              } else if (Date.now() - state.multipleFaceStart > 2000) {
                dispatch("triggerViolation", "Multiple persons detected");
              }
            } else {
              commit("SET_MULTIPLE_FACE_START", null);
              const landmarks = faces[0].keypoints;
              dispatch("detectEyesClosed", landmarks);
              dispatch("detectLookingAway", landmarks);
            }
          }
        } catch (err) {
          console.warn("Detection frame skipped:", err);
        }
      }

      currentLoopId = requestAnimationFrame(loop);
      commit("SET_ANIMATION_ID", currentLoopId);
    };

    currentLoopId = requestAnimationFrame(loop);
    commit("SET_ANIMATION_ID", currentLoopId);
  },

  stopDetection({ state, commit }) {
    if (state.animationId) {
      cancelAnimationFrame(state.animationId);
      commit("SET_ANIMATION_ID", null);
    }
    if (_verifyInterval) {
      clearInterval(_verifyInterval);
      _verifyInterval = null;
    }
  },

  detectEyesClosed({ state, commit, dispatch }, landmarks) {
    const LEFT_EYE = [33, 160, 158, 133, 153, 144];
    const RIGHT_EYE = [362, 385, 387, 263, 373, 380];

    const getEAR = (indices) => {
      const dist = (a, b) =>
        Math.hypot(
          landmarks[a].x - landmarks[b].x,
          landmarks[a].y - landmarks[b].y
        );
      return (
        (dist(indices[1], indices[5]) + dist(indices[2], indices[4])) /
        (2.0 * dist(indices[0], indices[3]))
      );
    };

    const avg = (getEAR(LEFT_EYE) + getEAR(RIGHT_EYE)) / 2;

    if (avg < 0.2) {
      if (!state.eyesClosedStart) {
        commit("SET_EYES_CLOSED_START", Date.now());
      } else if (Date.now() - state.eyesClosedStart > 2000) {
        dispatch("triggerViolation", "Eyes closed too long");
      }
    } else {
      commit("SET_EYES_CLOSED_START", null);
    }
  },

  detectLookingAway({ state, commit, dispatch }, landmarks) {
    const nose = landmarks[1];
    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const eyeCenterX = (leftEye.x + rightEye.x) / 2;
    const deviation = Math.abs(nose.x - eyeCenterX);

    if (deviation > 40) {
      if (!state.lookingAwayStart) {
        commit("SET_LOOKING_AWAY_START", Date.now());
      } else if (Date.now() - state.lookingAwayStart > 2000) {
        dispatch("triggerViolation", "Looking away");
      }
    } else {
      commit("SET_LOOKING_AWAY_START", null);
    }
  },

  triggerViolation({ state, commit, dispatch }, reason) {
    const now = Date.now();
    if (now - state.lastViolationTime < state.violationCooldown) return;

    commit("SET_LAST_VIOLATION_TIME", now);
    commit("PUSH_VIOLATION", { reason, time: new Date().toISOString() });

    if (!state.showViolationAlert) {
      commit("SET_VIOLATION_ALERT", { show: true, reason });
    }

    dispatch("logEvent", {
      event_type: "violation",
      log_data: { reason, timestamp: new Date().toISOString() },
    });
  },

  startProctoring({ state, commit, dispatch }) {
    if (state.listenersActive && _keydownHandler) return;

    // 1. Keyboard Handlers
    _keydownHandler = (e) => {
      const isCmdOrCtrl = e.ctrlKey || e.metaKey;

      // Prevent Copy, Paste, Cut (via Keyboard)
      if (isCmdOrCtrl && ["c", "C", "v", "V", "x", "X"].includes(e.key)) {
        e.preventDefault();
        const action =
          e.key.toLowerCase() === "c"
            ? "Copy"
            : e.key.toLowerCase() === "v"
            ? "Paste"
            : "Cut";
        dispatch("triggerViolation", `${action} Attempt detected`);
      }

      // Block Tab Switching Shortcuts
      // Ctrl + Tab (Windows/Linux/Mac)
      if (isCmdOrCtrl && e.key === "Tab") {
        e.preventDefault();
        dispatch("triggerViolation", "Tab switching shortcut detected");
      }

      // Block Screenshot
      if (e.key === "PrintScreen") {
        dispatch("triggerViolation", "Screenshot attempt detected");
      }

      // Block DevTools (F12, Ctrl+Shift+I, Ctrl+Shift+J)
      const isDevTools =
        e.key === "F12" ||
        (isCmdOrCtrl &&
          e.shiftKey &&
          (e.key === "I" || e.key === "i" || e.key === "J" || e.key === "j"));
      if (isDevTools) {
        e.preventDefault();
        dispatch("triggerViolation", "Developer tools access attempt");
      }

      // Block View Source (Ctrl + U)
      if (isCmdOrCtrl && (e.key === "u" || e.key === "U")) {
        e.preventDefault();
        dispatch("triggerViolation", "View source attempt detected");
      }

      // Warning for Reload
      if (e.key === "F5" || (isCmdOrCtrl && (e.key === "r" || e.key === "R"))) {
        e.preventDefault();
        dispatch("triggerViolation", "Page reload attempt detected");
      }
    };

    // 2. Native Clipboard Handlers (Directly catch context menu or menu actions)
    _copyHandler = (e) => {
      e.preventDefault();
      dispatch("triggerViolation", "Copy Attempt detected");
    };

    _pasteHandler = (e) => {
      e.preventDefault();
      dispatch("triggerViolation", "Paste Attempt detected");
    };

    _cutHandler = (e) => {
      e.preventDefault();
      dispatch("triggerViolation", "Cut Attempt detected");
    };

    // 2. Visibility / Blur Handlers
    _visibilityHandler = () => {
      if (_suppressFocusViolations) return;
      if (document.hidden) {
        dispatch("triggerViolation", "User switched browser tab");
      }
    };

    _blurHandler = () => {
      if (_suppressFocusViolations) return;
      dispatch("triggerViolation", "User switched application / lost focus");
    };

    // 3. Right Click
    _rightClickHandler = (e) => {
      e.preventDefault();
      dispatch("triggerViolation", "Right click attempt detected");
    };

    window.addEventListener("keydown", _keydownHandler);
    document.addEventListener("visibilitychange", _visibilityHandler);
    window.addEventListener("blur", _blurHandler);
    window.addEventListener("contextmenu", _rightClickHandler);

    // Clipboard listeners on document
    document.addEventListener("copy", _copyHandler);
    document.addEventListener("paste", _pasteHandler);
    document.addEventListener("cut", _cutHandler);

    commit("SET_LISTENERS", true);
  },

  stopProctoring({ commit }) {
    if (_keydownHandler) {
      window.removeEventListener("keydown", _keydownHandler);
      _keydownHandler = null;
    }
    if (_visibilityHandler) {
      document.removeEventListener("visibilitychange", _visibilityHandler);
      _visibilityHandler = null;
    }
    if (_blurHandler) {
      window.removeEventListener("blur", _blurHandler);
      _blurHandler = null;
    }
    if (_rightClickHandler) {
      window.removeEventListener("contextmenu", _rightClickHandler);
      _rightClickHandler = null;
    }

    if (_copyHandler) {
      document.removeEventListener("copy", _copyHandler);
      _copyHandler = null;
    }
    if (_pasteHandler) {
      document.removeEventListener("paste", _pasteHandler);
      _pasteHandler = null;
    }
    if (_cutHandler) {
      document.removeEventListener("cut", _cutHandler);
      _cutHandler = null;
    }

    commit("SET_LISTENERS", false);
  },

  dismissViolation({ commit }) {
    commit("SET_VIOLATION_ALERT", { show: false, reason: "" });
  },

  suppressFocusViolations({ duration = 5000 } = {}) {
    suppressFocus(duration);
  },

  async cleanup({ commit, dispatch }) {
    dispatch("stopDetection");
    dispatch("stopProctoring");

    if (_cameraStream) {
      _cameraStream.getTracks().forEach((t) => t.stop());
      _cameraStream = null;
    }
    if (_screenStream) {
      _screenStream.getTracks().forEach((t) => t.stop());
      _screenStream = null;
    }
    if (_room) {
      await _room.disconnect();
      _room = null;
    }
    if (_adminRoom) {
      await _adminRoom.disconnect();
      _adminRoom = null;
    }

    if (_verifyInterval) {
      clearInterval(_verifyInterval);
      _verifyInterval = null;
    }

    _detector = null;
    commit("RESET");
  },

  async performFaceVerification({ commit }, videoEl) {
    if (
      !videoEl ||
      videoEl.readyState < 2 ||
      videoEl.videoWidth === 0 ||
      videoEl.videoHeight === 0
    ) {
      return;
    }

    try {
      const canvas = document.createElement("canvas");
      canvas.width = videoEl.videoWidth;
      canvas.height = videoEl.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoEl, 0, 0);

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.8)
      );
      const formData = new FormData();
      formData.append("user_image", blob, "face.jpg");

      const response = await apiPost(
        "/proctoring/proctoring-face-verify/",
        formData
      );

      if (response.data.success) {
        commit("SET_LAST_FACE_VERIFICATION_STATUS", "success");
      } else {
        commit("SET_LAST_FACE_VERIFICATION_STATUS", "failed");
        console.warn(
          "[Proctoring] Face verification failed:",
          response.data.message
        );
      }
    } catch (err) {
      console.error("[Proctoring] Face verification error:", err);
      commit("SET_LAST_FACE_VERIFICATION_STATUS", "error");
    }
  },

  async terminateExam({ dispatch, commit }) {
    await dispatch("cleanup");
    commit("SET_IS_EXAM_STARTED", false);
    // Immediate redirect to prevent remaining on the exam page
    window.location.href = "/";
  },

  async detectProhibitedObjects({ state, commit, dispatch }, videoEl) {
    if (!_objectDetector) {
      commit("SET_OBJECT_DETECTED_START", null);
      commit("SET_DETECTED_OBJECTS", []);
      return;
    }

    if (
      _objectDetectInFlight ||
      !videoEl ||
      videoEl.readyState < 2 ||
      !videoEl.videoWidth ||
      !videoEl.videoHeight
    ) {
      return;
    }

    _objectDetectInFlight = true;
    try {
      try {
        const mem = tf.memory();
        if (mem?.numTensors && mem.numTensors > 250) {
          console.warn(
            "[Proctoring] Skipping object detect due to high tensor count:",
            mem.numTensors
          );
          return;
        }
      } catch (err) {
        // ignore
      }

      if (!_objectCanvas) {
        _objectCanvas = document.createElement("canvas");
        _objectCanvasCtx = _objectCanvas.getContext("2d", {
          willReadFrequently: true,
        });
      }

      const vw = videoEl.videoWidth;
      const vh = videoEl.videoHeight;
      if (!_objectTargetW) {
        _objectTargetW = 480;
      }
      const targetW = Math.min(_objectTargetW, vw);
      const targetH = Math.max(1, Math.round((vh / vw) * targetW));

      _objectCanvas.width = targetW;
      _objectCanvas.height = targetH;
      _objectCanvasCtx.drawImage(videoEl, 0, 0, targetW, targetH);

      const predictions = await _objectDetector.detect(_objectCanvas);

      const prohibited = new Set([
        "cell phone",
        "mobile phone",
        "phone",
        "laptop",
        "tv",
        "tvmonitor",
        "monitor",
        "book",
      ]);

      const minScoreByClass = {
        "cell phone": 0.12,
        "mobile phone": 0.12,
        phone: 0.12,
        laptop: 0.18,
        tv: 0.18,
        tvmonitor: 0.18,
        monitor: 0.18,
        book: 0.18,
      };

      const foundProhibited = (predictions || [])
        .filter((p) => {
          if (!prohibited.has(p.class)) return false;
          const min = minScoreByClass[p.class] ?? 0.18;
          return (p.score || 0) >= min;
        })
        .sort((a, b) => b.score - a.score);

      if (foundProhibited.length > 0) {
        const itemName = foundProhibited[0].class;

        if (!state.objectDetectedStart) {
          commit("SET_OBJECT_DETECTED_START", Date.now());
        } else if (Date.now() - state.objectDetectedStart > 800) {
          dispatch(
            "triggerViolation",
            `Prohibited object detected: ${itemName}`
          );
        }
      } else {
        commit("SET_OBJECT_DETECTED_START", null);
      }

      commit(
        "SET_DETECTED_OBJECTS",
        (predictions || []).map((p) => p.class)
      );
    } catch (err) {
      if (_objectTargetW && _objectTargetW > 480) {
        _objectTargetW = Math.max(480, Math.round(_objectTargetW * 0.75));
      }
      commit("SET_OBJECT_DETECTED_START", null);
      commit("SET_DETECTED_OBJECTS", []);
    } finally {
      _objectDetectInFlight = false;
    }
  },

  async createEvent(_, status) {
    try {
      const student = store.state.user?.id;
      const exam = localStorage.getItem("exam_id");

      if (status === "COMPLETED") {
        const attemptId = localStorage.getItem("exam_attempt_id");
        if (attemptId) {
          await apiPatch(`/exam-delivery/exam-attempt-update/${attemptId}/`, {
            student: student,
            exam,
            status: status,
          });
          window.dispatchEvent(new CustomEvent("exam-completed"));
          return;
        }
      }

      const res = await apiPost("/exam-delivery/exam-attempt-create/", {
        student: student,
        exam,
        status: status,
      });

      const attemptId = res.data?.data?.id;
      if (attemptId) {
        localStorage.setItem("exam_attempt_id", attemptId);
      }

      if (status === "COMPLETED") {
        window.dispatchEvent(new CustomEvent("exam-completed"));
      }
    } catch (err) {
      console.error("[Proctoring] Log event failed:", err);
      throw err;
    }
  },

  async logEvent(_, { event_type, log_data }) {
    try {
      const student_id = store.state.user?.id;
      const exam_id = localStorage.getItem("exam_id");

      await apiPost("/exam-delivery/attempt-event-create/", {
        event_type,
        log_data,
        student_id: student_id,
        exam_id,
      });
    } catch (err) {
      console.error("[Proctoring] Log event failed:", err);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
